import { enhanceChartColorPicker } from "./chart-color-picker.js";
import { createChartIcon } from "./chart-icon.js";

const WIDTH_OPTIONS = Object.freeze([1, 2, 3, 4, 5]);
const LINE_STYLE_OPTIONS = Object.freeze([
  ["solid", "Solid"],
  ["dashed", "Dashed"],
  ["dotted", "Dotted"]
]);

export class DrawingContextMenu {
  constructor({
    stage,
    portal = stage,
    onAction,
    onStylePreview,
    onStyleCommit,
    onClose
  }) {
    this.stage = stage;
    this.portal = portal instanceof HTMLElement ? portal : stage;
    this.onAction = onAction;
    this.onStylePreview = onStylePreview;
    this.onStyleCommit = onStyleCommit;
    this.onClose = onClose;
    this.drawing = null;
    this.capabilities = null;
    this.destinationCharts = [];
    this.activeSubmenu = null;
    this.returnFocus = null;
    this.styleControls = new Map();
    this.settingControls = new Map();
    this.channelFillInput = null;
    this.channelFillPicker = null;

    this.element = document.createElement("div");
    this.element.className = "chart-context-menu chart-drawing-context-menu";
    this.element.dataset.drawingContextMenu = "";
    this.element.setAttribute("role", "menu");
    this.element.setAttribute("aria-label", "Selected drawing actions");
    this.element.hidden = true;
    this.portal.append(this.element);
    this.element.addEventListener("keydown", (event) => this.handleKeydown(event));
    this.element.addEventListener("contextmenu", (event) => event.preventDefault());
  }

  open({
    drawing,
    capabilities,
    point,
    destinationCharts = [],
    returnFocus = null
  }) {
    this.close({ restoreFocus: false, notify: false });
    this.drawing = drawing;
    this.capabilities = capabilities;
    this.destinationCharts = destinationCharts;
    this.returnFocus = returnFocus;
    this.build();
    this.element.hidden = false;
    this.element.style.left = "0px";
    this.element.style.top = "0px";
    const bounds = this.portal.getBoundingClientRect();
    const stageBounds = this.stage.getBoundingClientRect();
    const offsetX = stageBounds.left - bounds.left;
    const offsetY = stageBounds.top - bounds.top;
    const inset = 6;
    this.element.classList.toggle("is-narrow", stageBounds.width < 286);
    const left = clamp(
      offsetX + point.x,
      inset,
      Math.max(inset, bounds.width - this.element.offsetWidth - inset)
    );
    const top = clamp(
      offsetY + point.y,
      inset,
      Math.max(inset, bounds.height - this.element.offsetHeight - inset)
    );
    this.element.style.left = `${Math.round(left)}px`;
    this.element.style.top = `${Math.round(top)}px`;
    this.mainButtons()[0]?.focus();
  }

  close({ restoreFocus = false, notify = true } = {}) {
    if (!this.element || this.element.hidden) return;
    this.colorPicker?.close();
    this.channelFillPicker?.close();
    this.closeSubmenu({ restoreFocus: false });
    this.element.hidden = true;
    if (notify) this.onClose?.();
    if (restoreFocus) this.returnFocus?.focus?.({ preventScroll: true });
  }

  updateDrawing(drawing) {
    if (!drawing || drawing.id !== this.drawing?.id) {
      this.close({ restoreFocus: false });
      return;
    }
    this.drawing = drawing;
    this.syncStyleControls();
    this.syncSettingControls();
    this.syncChannelFillControl();
    const lockButton = this.element.querySelector("[data-drawing-context-action='lock']");
    if (lockButton instanceof HTMLButtonElement) {
      const label = drawing.locked ? "Unlock" : "Lock";
      lockButton.replaceChildren(createChartIcon(drawing.locked ? "unlock" : "lock"));
      lockButton.setAttribute("aria-label", label);
      lockButton.title = label;
    }
  }

  contains(target) {
    return target instanceof Node && (
      this.element.contains(target)
      || Boolean(this.colorPicker?.popover?.contains(target))
      || Boolean(this.channelFillPicker?.popover?.contains(target))
    );
  }

  destroy() {
    this.colorPicker?.destroy();
    this.channelFillPicker?.destroy();
    this.element.remove();
  }

  build() {
    this.colorPicker?.destroy();
    this.channelFillPicker?.destroy();
    this.colorPicker = null;
    this.channelFillPicker = null;
    this.channelFillInput = null;
    this.styleControls.clear();
    this.settingControls.clear();
    this.element.replaceChildren();
    const appearance = new Set(this.capabilities?.appearance || []);
    const actions = new Set(this.capabilities?.actions || []);
    const channelSettings = appearance.has("fillColor")
      ? this.buildChannelSettings(appearance)
      : null;

    if (appearance.size > 0 || actions.size > 0) {
      this.buildStyleStrip(appearance, actions, channelSettings);
    }

    if (this.drawing?.type === "horizontal-line") {
      this.element.append(this.buildDivider());
      const alertButton = document.createElement("button");
      alertButton.type = "button";
      alertButton.role = "menuitem";
      alertButton.dataset.drawingContextAction = "alert";
      alertButton.append(
        createChartIcon("alert"),
        Object.assign(document.createElement("span"), {
          textContent: this.drawing.metadata?.alertId ? "Edit Alert" : "Add Alert"
        })
      );
      alertButton.addEventListener("click", () => this.onAction?.("alert"));
      this.element.append(alertButton);
    }

    this.status = document.createElement("span");
    this.status.className = "chart-visually-hidden";
    this.status.setAttribute("aria-live", "polite");
    this.status.setAttribute("aria-atomic", "true");
    this.element.append(this.status);
  }

  buildStyleStrip(appearance, actions, channelSettings = null) {
    const strip = document.createElement("div");
    strip.className = "drawing-context-style-strip";
    strip.setAttribute("role", "group");
    strip.setAttribute("aria-label", "Drawing properties and actions");

    if (appearance.has("color")) {
      strip.append(this.buildColorControl(appearance.has("opacity")));
    }
    if (appearance.has("lineStyle")) {
      strip.append(this.buildChoiceControl({
        setting: "lineStyle",
        action: "style-lineStyle",
        accessibleName: "Change line style",
        options: LINE_STYLE_OPTIONS,
        selected: this.drawing.style.lineStyle,
        menuLabel: "Drawing line style",
        renderValue: (value) => {
          const sample = document.createElement("span");
          sample.className = `drawing-style-line-sample is-${value}`;
          sample.setAttribute("aria-hidden", "true");
          return sample;
        },
        valueLabel: (value) => optionLabel(LINE_STYLE_OPTIONS, value)
      }));
    }
    if (appearance.has("width")) {
      strip.append(this.buildChoiceControl({
        setting: "width",
        action: "style-width",
        accessibleName: "Change line thickness",
        options: WIDTH_OPTIONS.map((value) => [value, `${value} px`]),
        selected: this.drawing.style.width,
        menuLabel: "Drawing width",
        renderValue: (value) => textValue(`${value} px`),
        valueLabel: (value) => `${value} px`,
        previewClass: "drawing-width-preview"
      }));
    }
    if (actions.has("lock")) {
      strip.append(this.buildToolbarAction(
        this.drawing.locked ? "Unlock" : "Lock",
        "lock",
        this.drawing.locked ? "unlock" : "lock"
      ));
    }
    if (actions.has("clone")) {
      strip.append(this.buildToolbarAction("Clone", "clone", "clone"));
    }
    if (actions.has("copy")) strip.append(this.buildCopyControl());
    if (actions.has("delete")) {
      strip.append(this.buildToolbarAction("Delete", "delete", "trash", "is-danger"));
    }
    if (channelSettings) {
      strip.append(this.buildChannelSettingsControl(channelSettings));
    }

    this.element.append(strip);
  }

  buildDivider() {
    const divider = document.createElement("div");
    divider.className = "chart-context-divider";
    divider.setAttribute("role", "separator");
    return divider;
  }

  buildColorControl(showOpacity) {
    const control = document.createElement("div");
    const input = document.createElement("input");
    control.className = "drawing-style-control drawing-style-color-control";
    input.type = "color";
    input.value = this.drawing.style.color;
    input.dataset.chartColorPicker = "";
    input.dataset.chartOpacity = String(this.drawing.style.opacity);
    input.setAttribute(
      "aria-label",
      showOpacity ? "Change color and transparency" : "Change color"
    );
    control.append(input);
    this.colorPicker = enhanceChartColorPicker(input, {
      showOpacity,
      closeOnCommit: true
    });
    const trigger = this.colorPicker.trigger;
    trigger.classList.add("drawing-style-trigger", "drawing-style-color-trigger");
    trigger.setAttribute("role", "menuitem");
    trigger.querySelector(".chart-color-trigger-chevron")?.remove();
    this.styleControls.set("color", { trigger, input });
    input.addEventListener("input", () => {
      this.drawing.style.color = input.value;
      this.syncStyleControls();
      this.onStylePreview?.("color", input.value);
    });
    input.addEventListener("change", () => {
      this.drawing.style.color = input.value;
      this.syncStyleControls();
      this.onStyleCommit?.("color", input.value);
      this.announceStyleChange("Color", input.value);
    });
    input.addEventListener("chart-color-opacity", (event) => {
      const opacity = Number(event.detail?.opacity);
      if (!Number.isFinite(opacity)) return;
      this.drawing.style.opacity = opacity;
      this.syncStyleControl("color");
      if (event.detail?.commit) {
        this.onStyleCommit?.("opacity", opacity);
        this.announceStyleChange("Transparency", `${100 - opacity}%`);
      } else {
        this.onStylePreview?.("opacity", opacity);
      }
    });
    this.syncStyleControl("color");
    return control;
  }

  buildChannelSettings(appearance) {
    const panel = document.createElement("div");
    panel.className = "chart-context-menu drawing-context-submenu drawing-channel-settings";
    panel.setAttribute("role", "menu");
    panel.setAttribute("aria-label", "Drawing channel settings");
    panel.hidden = true;

    const fillSection = this.addSettingSection(panel, "Fill");
    this.addChannelFillControl(fillSection);

    const displaySection = this.addSettingSection(panel, "Display");
    const displayGrid = this.addSettingGrid(displaySection);
    this.addSettingControl(displayGrid, {
      label: "Visible",
      scope: "drawing",
      setting: "visible",
      type: "checkbox",
      value: this.drawing.visible
    });

    const projectionSection = this.addSettingSection(panel, "Projection");
    const projectionGrid = this.addSettingGrid(projectionSection);
    this.addSettingControl(projectionGrid, {
      label: "Extend left",
      scope: "style",
      setting: "extendLeft",
      type: "checkbox",
      value: this.drawing.style.extendLeft
    });
    this.addSettingControl(projectionGrid, {
      label: "Extend right",
      scope: "style",
      setting: "extendRight",
      type: "checkbox",
      value: this.drawing.style.extendRight
    });

    if (appearance.has("source")) {
      this.addSettingControl(displayGrid, {
        label: "Show fill",
        scope: "setting",
        setting: "showFill",
        type: "checkbox",
        value: this.drawing.settings.showFill
      });

      const calculationSection = this.addSettingSection(panel, "Calculation");
      this.addSettingControl(calculationSection, {
        label: "Source",
        scope: "setting",
        setting: "source",
        type: "select",
        options: [
          ["open", "Open"],
          ["high", "High"],
          ["low", "Low"],
          ["close", "Close"],
          ["hl2", "HL2"],
          ["hlc3", "HLC3"],
          ["ohlc4", "OHLC4"]
        ],
        value: this.drawing.settings.source
      });
      for (const [setting, label] of [
        ["upperDeviation", "Upper deviation"],
        ["lowerDeviation", "Lower deviation"]
      ]) {
        this.addSettingControl(calculationSection, {
          label,
          scope: "setting",
          setting,
          type: "number",
          minimum: 0,
          maximum: 20,
          step: 0.25,
          value: this.drawing.settings[setting]
        });
      }

      const linesSection = this.addSettingSection(panel, "Lines");
      const linesGrid = this.addSettingGrid(linesSection);
      for (const [setting, label] of [
        ["showCenterLine", "Center line"],
        ["showUpperLine", "Upper line"],
        ["showLowerLine", "Lower line"]
      ]) {
        this.addSettingControl(linesGrid, {
          label,
          scope: "setting",
          setting,
          type: "checkbox",
          value: this.drawing.settings[setting]
        });
      }

      const centerLineSection = this.addSettingSection(panel, "Center line");
      this.addSettingControl(centerLineSection, {
        label: "Color",
        scope: "setting",
        setting: "centerColor",
        type: "color",
        value: this.drawing.settings.centerColor
      });
      this.addSettingControl(centerLineSection, {
        label: "Width",
        scope: "setting",
        setting: "centerWidth",
        type: "number",
        minimum: 1,
        maximum: 5,
        step: 1,
        value: this.drawing.settings.centerWidth
      });
      this.addSettingControl(centerLineSection, {
        label: "Style",
        scope: "setting",
        setting: "centerLineStyle",
        type: "select",
        options: LINE_STYLE_OPTIONS,
        value: this.drawing.settings.centerLineStyle
      });
    }
    return panel;
  }

  addSettingSection(panel, label) {
    const section = document.createElement("div");
    const heading = document.createElement("span");
    section.className = "drawing-channel-settings-section";
    section.setAttribute("role", "group");
    section.setAttribute("aria-label", label);
    heading.className = "drawing-channel-settings-heading";
    heading.textContent = label;
    heading.setAttribute("aria-hidden", "true");
    section.append(heading);
    panel.append(section);
    return section;
  }

  addSettingGrid(section) {
    const grid = document.createElement("div");
    grid.className = "drawing-channel-toggle-grid";
    section.append(grid);
    return grid;
  }

  addChannelFillControl(section) {
    const row = document.createElement("div");
    const name = document.createElement("span");
    const input = document.createElement("input");
    row.className = "drawing-channel-setting-row is-color-picker";
    name.textContent = "Color";
    input.type = "color";
    input.value = this.drawing.style.fillColor;
    input.dataset.chartOpacity = String(this.drawing.style.fillOpacity);
    input.setAttribute("aria-label", "Fill color");
    row.append(name, input);
    section.append(row);

    this.channelFillInput = input;
    this.channelFillPicker = enhanceChartColorPicker(input, {
      showOpacity: true,
      closeOnCommit: false
    });
    input.addEventListener("input", () => {
      this.drawing.style.fillColor = input.value;
      this.syncChannelFillControl();
      this.onStylePreview?.("fillColor", input.value);
    });
    input.addEventListener("change", () => {
      this.drawing.style.fillColor = input.value;
      this.syncChannelFillControl();
      this.onStyleCommit?.("fillColor", input.value);
      this.announceStyleChange("Fill color", input.value);
    });
    input.addEventListener("chart-color-opacity", (event) => {
      const opacity = Number(event.detail?.opacity);
      if (!Number.isFinite(opacity)) return;
      this.drawing.style.fillOpacity = opacity;
      this.syncChannelFillControl();
      if (event.detail?.commit) {
        this.onStyleCommit?.("fillOpacity", opacity);
        this.announceStyleChange("Fill opacity", `${opacity}%`);
      } else {
        this.onStylePreview?.("fillOpacity", opacity);
      }
    });
    this.syncChannelFillControl();
  }

  syncChannelFillControl() {
    if (!this.channelFillInput || !this.channelFillPicker || !this.drawing) return;
    const color = this.drawing.style.fillColor;
    const opacity = this.drawing.style.fillOpacity;
    this.channelFillInput.value = color;
    this.channelFillInput.dataset.chartOpacity = String(opacity);
    this.channelFillPicker.sync(color);
    this.channelFillPicker.syncOpacity(opacity);
    this.channelFillPicker.trigger.setAttribute(
      "aria-label",
      `Fill color and opacity, current value ${color}, ${opacity}%`
    );
    this.channelFillPicker.trigger.title = `Fill ${color} · Opacity ${opacity}%`;
  }

  buildChannelSettingsControl(submenu) {
    const wrapper = document.createElement("div");
    const button = document.createElement("button");
    const label = this.drawing.type === "regression-channel"
      ? "More regression settings"
      : "More channel settings";
    wrapper.className = [
      "drawing-style-control",
      "drawing-style-action-control",
      "drawing-context-submenu-anchor"
    ].join(" ");
    button.type = "button";
    button.className = [
      "drawing-style-trigger",
      "drawing-style-action-trigger",
      "drawing-channel-settings-trigger"
    ].join(" ");
    button.dataset.drawingContextAction = "channel-settings";
    button.setAttribute("role", "menuitem");
    button.setAttribute("aria-label", label);
    button.setAttribute("aria-haspopup", "menu");
    button.setAttribute("aria-expanded", "false");
    button.title = label;
    button.append(createChartIcon("more"));
    button.addEventListener("click", () => this.openSubmenu(button, submenu, "below"));
    wrapper.append(button, submenu);
    return wrapper;
  }

  addSettingControl(panel, {
    label,
    scope,
    setting,
    type,
    value,
    options = [],
    minimum = null,
    maximum = null,
    step = null
  }) {
    const row = document.createElement("label");
    const name = document.createElement("span");
    const input = type === "select"
      ? document.createElement("select")
      : document.createElement("input");
    row.className = "drawing-channel-setting-row";
    row.classList.add(`is-${type}`);
    name.textContent = label;
    if (type === "select") {
      for (const [optionValue, optionLabelText] of options) {
        const option = document.createElement("option");
        option.value = String(optionValue);
        option.textContent = optionLabelText;
        input.append(option);
      }
      input.value = String(value);
    } else {
      input.type = type;
      if (type === "checkbox") input.checked = Boolean(value);
      else input.value = String(value);
      if (minimum !== null) input.min = String(minimum);
      if (maximum !== null) input.max = String(maximum);
      if (step !== null) input.step = String(step);
    }
    input.setAttribute("aria-label", label);
    input.addEventListener("change", () => {
      const nextValue = type === "checkbox"
        ? input.checked
        : type === "number"
          ? Number(input.value)
          : input.value;
      this.applySetting(scope, setting, nextValue);
    });
    row.append(name, input);
    panel.append(row);
    this.settingControls.set(`${scope}:${setting}`, { input, type });
  }

  applySetting(scope, setting, value) {
    const action = scope === "style"
      ? `style-${setting}`
      : scope === "setting"
        ? `setting-${setting}`
        : "drawing-visible";
    const changed = this.onAction?.(action, value);
    if (changed === false) {
      this.syncSettingControls();
      return;
    }
    if (scope === "style") this.drawing.style[setting] = value;
    else if (scope === "setting") this.drawing.settings[setting] = value;
    else this.drawing.visible = Boolean(value);
    this.syncSettingControls();
    this.announceStyleChange(labelForSetting(setting), String(value));
  }

  syncSettingControls() {
    for (const [key, control] of this.settingControls) {
      const [scope, setting] = key.split(":");
      const value = scope === "style"
        ? this.drawing?.style?.[setting]
        : scope === "setting"
          ? this.drawing?.settings?.[setting]
          : this.drawing?.visible;
      if (control.type === "checkbox") control.input.checked = Boolean(value);
      else if (value !== undefined) control.input.value = String(value);
    }
  }

  buildToolbarAction(label, action, icon, className = "") {
    const wrapper = document.createElement("div");
    const button = document.createElement("button");
    wrapper.className = "drawing-style-control drawing-style-action-control";
    button.type = "button";
    button.className = "drawing-style-trigger drawing-style-action-trigger";
    button.dataset.drawingContextAction = action;
    button.setAttribute("role", "menuitem");
    button.setAttribute("aria-label", label);
    button.title = label;
    if (className) button.classList.add(className);
    button.append(createChartIcon(icon));
    button.addEventListener("click", () => this.runAction(action));
    wrapper.append(button);
    return wrapper;
  }

  buildChoiceControl({
    setting,
    action,
    accessibleName,
    options,
    selected,
    menuLabel,
    renderValue,
    valueLabel,
    previewClass = ""
  }) {
    const wrapper = document.createElement("div");
    const trigger = document.createElement("button");
    const popover = this.buildChoiceSubmenu(
      options,
      selected,
      action,
      setting,
      previewClass,
      menuLabel
    );
    wrapper.className = "drawing-style-control drawing-context-submenu-anchor";
    trigger.type = "button";
    trigger.className = "drawing-style-trigger";
    trigger.dataset.drawingContextAction = action;
    trigger.setAttribute("role", "menuitem");
    trigger.setAttribute("aria-haspopup", "menu");
    trigger.setAttribute("aria-expanded", "false");
    trigger.title = accessibleName;
    trigger.append(renderValue(selected));
    trigger.addEventListener("click", () => this.openSubmenu(trigger, popover, "below"));
    wrapper.append(trigger, popover);
    this.styleControls.set(setting, {
      trigger,
      popover,
      accessibleName,
      renderValue,
      valueLabel
    });
    this.syncStyleControl(setting);
    return wrapper;
  }

  buildChoiceSubmenu(
    options,
    selected,
    action,
    setting,
    previewClass = "",
    menuLabel = ""
  ) {
    const menu = document.createElement("div");
    menu.className = "chart-context-menu drawing-context-submenu drawing-style-popover";
    menu.setAttribute("role", "menu");
    if (menuLabel) menu.setAttribute("aria-label", menuLabel);
    menu.hidden = true;
    for (const [value, label] of options) {
      const button = this.createButton(label, action);
      button.dataset.drawingStyleValue = String(value);
      button.setAttribute("role", "menuitemradio");
      button.setAttribute("aria-checked", String(value === selected));
      const check = document.createElement("span");
      check.className = "drawing-context-check";
      check.textContent = "\u2713";
      check.setAttribute("aria-hidden", "true");
      if (previewClass) {
        const preview = document.createElement("span");
        preview.className = `${previewClass} is-${value}`;
        button.prepend(preview);
      } else if (setting === "lineStyle") {
        const preview = document.createElement("span");
        preview.className = `drawing-line-style-preview is-${value}`;
        button.prepend(preview);
      }
      button.append(check);
      button.addEventListener("click", () => {
        const changed = this.onAction?.(action, value);
        if (changed === false) return;
        this.drawing.style[setting] = value;
        this.syncStyleControl(setting);
        this.closeSubmenu({ restoreFocus: true });
        this.announceStyleChange(
          setting === "lineStyle"
            ? "Line style"
            : setting === "width"
              ? "Line thickness"
              : "Opacity",
          label
        );
      });
      menu.append(button);
    }
    return menu;
  }

  buildCopyControl() {
    const copyMenu = document.createElement("div");
    copyMenu.className = "chart-context-menu drawing-context-submenu";
    copyMenu.setAttribute("role", "menu");
    copyMenu.setAttribute("aria-label", "Copy to charts");
    copyMenu.hidden = true;
    if (this.destinationCharts.length === 0) {
      const empty = this.createButton("No other charts available", "copy-disabled");
      empty.disabled = true;
      empty.title = "Open another chart to copy this drawing.";
      copyMenu.append(empty);
    } else {
      for (const chart of this.destinationCharts) {
        const button = this.createButton(chart.label, "copy-chart");
        button.addEventListener("click", () => this.runAction("copy-chart", chart.id));
        copyMenu.append(button);
      }
      if (this.destinationCharts.length > 1) {
        copyMenu.append(this.createDivider());
        const all = this.createButton("All other charts", "copy-all");
        all.addEventListener("click", () => this.runAction("copy-all"));
        copyMenu.append(all);
      }
    }
    const wrapper = document.createElement("div");
    const button = document.createElement("button");
    wrapper.className = [
      "drawing-style-control",
      "drawing-style-action-control",
      "drawing-context-submenu-anchor"
    ].join(" ");
    button.type = "button";
    button.className = "drawing-style-trigger drawing-style-action-trigger";
    button.dataset.drawingContextAction = "copy";
    button.setAttribute("role", "menuitem");
    button.setAttribute("aria-label", "Copy to charts");
    button.setAttribute("aria-haspopup", "menu");
    button.setAttribute("aria-expanded", "false");
    button.title = "Copy to charts";
    button.append(createChartIcon("copy"));
    button.addEventListener("click", () => this.openSubmenu(button, copyMenu, "below"));
    wrapper.append(button, copyMenu);
    return wrapper;
  }

  syncStyleControls() {
    for (const setting of this.styleControls.keys()) this.syncStyleControl(setting);
  }

  syncStyleControl(setting) {
    const control = this.styleControls.get(setting);
    const value = this.drawing?.style?.[setting];
    if (!control) return;
    if (setting === "color") {
      control.input.value = value;
      control.input.dataset.chartOpacity = String(this.drawing.style.opacity);
      this.colorPicker?.sync(value);
      this.colorPicker?.syncOpacity(this.drawing.style.opacity);
      const includesOpacity = this.capabilities?.appearance?.includes("opacity");
      const currentDescription = includesOpacity
        ? `${value}, opacity ${this.drawing.style.opacity}%`
        : value;
      control.trigger.setAttribute(
        "aria-label",
        `${includesOpacity ? "Change color and transparency" : "Change color"}, current value ${currentDescription}`
      );
      control.trigger.title = includesOpacity
        ? `Color ${value} · Opacity ${this.drawing.style.opacity}%`
        : `Change color (${value})`;
      return;
    }
    control.trigger.replaceChildren(control.renderValue(value));
    const currentLabel = control.valueLabel(value);
    control.trigger.setAttribute(
      "aria-label",
      `${control.accessibleName}, current value ${currentLabel}`
    );
    control.trigger.title = `${control.accessibleName} (${currentLabel})`;
    for (const option of control.popover.querySelectorAll("[role='menuitemradio']")) {
      option.setAttribute(
        "aria-checked",
        String(option.dataset.drawingStyleValue === String(value))
      );
    }
  }

  announceStyleChange(label, value) {
    if (this.status) this.status.textContent = `${label} changed to ${value}.`;
  }

  addAction(label, action, icon, className = "") {
    const button = this.createButton(label, action, icon);
    if (className) button.classList.add(className);
    button.addEventListener("click", () => this.runAction(action));
    this.element.append(button);
  }

  addSubmenu(label, action, submenu, icon = "") {
    const button = this.createButton(label, action, icon);
    button.setAttribute("aria-haspopup", "menu");
    button.setAttribute("aria-expanded", "false");
    const caret = document.createElement("span");
    caret.className = "drawing-context-caret";
    caret.textContent = "\u203a";
    caret.setAttribute("aria-hidden", "true");
    button.append(caret);
    button.addEventListener("click", () => this.openSubmenu(button, submenu, "side"));
    const wrapper = document.createElement("div");
    wrapper.className = "drawing-context-submenu-anchor";
    wrapper.append(button, submenu);
    this.element.append(wrapper);
  }

  openSubmenu(button, submenu, placement = "side") {
    if (this.colorPicker?.isOpen()) this.colorPicker.close();
    if (this.channelFillPicker?.isOpen()) this.channelFillPicker.close();
    if (this.activeSubmenu && this.activeSubmenu !== submenu) {
      this.closeSubmenu({ restoreFocus: false });
    }
    const opening = submenu.hidden;
    submenu.hidden = !opening;
    button.setAttribute("aria-expanded", String(opening));
    this.activeSubmenu = opening ? submenu : null;
    if (opening) {
      positionSubmenu(this.stage, this.element, button, submenu, placement);
      this.submenuButtons(submenu)[0]?.focus();
    }
  }

  closeSubmenu({ restoreFocus = false } = {}) {
    if (!this.activeSubmenu) return;
    this.channelFillPicker?.close();
    const submenu = this.activeSubmenu;
    const trigger = submenu.previousElementSibling;
    submenu.hidden = true;
    trigger?.setAttribute("aria-expanded", "false");
    this.activeSubmenu = null;
    if (restoreFocus) trigger?.focus();
  }

  runAction(action, value = null) {
    this.onAction?.(action, value);
    this.close({ restoreFocus: true });
  }

  createButton(label, action, icon = "") {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.drawingContextAction = action;
    button.setAttribute("role", "menuitem");
    if (icon) {
      button.append(createChartIcon(icon), createActionLabel(label));
      button.setAttribute("aria-label", label);
    } else {
      button.textContent = label;
    }
    return button;
  }

  createDivider() {
    const divider = document.createElement("div");
    divider.className = "chart-context-divider";
    divider.setAttribute("role", "separator");
    return divider;
  }

  addDivider() {
    this.element.append(this.createDivider());
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (this.activeSubmenu) {
        this.closeSubmenu({ restoreFocus: true });
      } else {
        this.close({ restoreFocus: true });
      }
      return;
    }
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      return;
    }
    const submenu = document.activeElement?.closest?.(".drawing-context-submenu");
    if (event.key === "ArrowLeft" && submenu) {
      event.preventDefault();
      this.closeSubmenu({ restoreFocus: true });
      return;
    }
    if (event.key === "ArrowRight") {
      const button = document.activeElement;
      const nested = button?.nextElementSibling;
      if (button?.getAttribute?.("aria-haspopup") === "menu" && nested) {
        event.preventDefault();
        this.openSubmenu(
          button,
          nested,
          button.closest(".drawing-context-style-strip") ? "below" : "side"
        );
      }
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const buttons = submenu ? this.submenuButtons(submenu) : this.mainButtons();
    if (buttons.length === 0) return;
    const current = buttons.indexOf(document.activeElement);
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? buttons.length - 1
        : event.key === "ArrowDown"
          ? (current + 1 + buttons.length) % buttons.length
          : (current - 1 + buttons.length) % buttons.length;
    buttons[next].focus();
  }

  mainButtons() {
    return [...this.element.querySelectorAll([
      ":scope > button:not(:disabled)",
      ":scope > .drawing-context-submenu-anchor > button:not(:disabled)",
      ":scope > .drawing-context-style-strip .drawing-style-trigger:not(:disabled)"
    ].join(", "))];
  }

  submenuButtons(submenu) {
    return [...submenu.querySelectorAll(":scope > button:not(:disabled)")];
  }
}

function positionSubmenu(stage, root, trigger, submenu, placement) {
  submenu.classList.remove("is-left", "is-above");
  submenu.style.left = "";
  submenu.style.right = "";
  submenu.style.top = "";
  submenu.style.bottom = "";
  submenu.style.maxHeight = "";
  const stageBounds = stage.getBoundingClientRect();
  const rootBounds = root.getBoundingClientRect();
  const triggerBounds = trigger.getBoundingClientRect();
  const inset = 6;
  const boundary = {
    left: Math.max(inset, stageBounds.left + inset),
    right: Math.min(window.innerWidth - inset, stageBounds.right - inset),
    top: Math.max(inset, stageBounds.top + inset),
    bottom: Math.min(window.innerHeight - inset, stageBounds.bottom - inset)
  };
  const computedMaxHeight = Number.parseFloat(getComputedStyle(submenu).maxHeight);
  const boundaryHeight = Math.max(0, boundary.bottom - boundary.top);
  if (Number.isFinite(computedMaxHeight) && computedMaxHeight > boundaryHeight) {
    submenu.style.maxHeight = `${Math.floor(boundaryHeight)}px`;
  }
  const submenuBounds = submenu.getBoundingClientRect();

  if (placement === "below") {
    const preferredLeft = triggerBounds.left;
    const left = clamp(
      preferredLeft,
      boundary.left,
      Math.max(boundary.left, boundary.right - submenuBounds.width)
    );
    const roomBelow = boundary.bottom - triggerBounds.bottom;
    const roomAbove = triggerBounds.top - boundary.top;
    const preferredTop = submenuBounds.height <= roomBelow || roomBelow >= roomAbove
      ? triggerBounds.bottom + inset
      : triggerBounds.top - submenuBounds.height - inset;
    const top = clamp(
      preferredTop,
      boundary.top,
      Math.max(boundary.top, boundary.bottom - submenuBounds.height)
    );
    submenu.style.left = `${Math.round(left - triggerBounds.left)}px`;
    const initialTopOffset = Math.round(top - triggerBounds.top);
    submenu.style.top = `${initialTopOffset}px`;
    const positionedBounds = submenu.getBoundingClientRect();
    const correctedTop = clamp(
      positionedBounds.top,
      boundary.top,
      Math.max(boundary.top, boundary.bottom - positionedBounds.height)
    );
    submenu.style.top = `${Math.round(
      initialTopOffset + correctedTop - positionedBounds.top
    )}px`;
    submenu.classList.toggle("is-above", correctedTop < triggerBounds.top);
    return;
  }

  const openLeft = rootBounds.right + submenuBounds.width + inset > boundary.right;
  submenu.classList.toggle("is-left", openLeft);
  const proposedTop = triggerBounds.top;
  const top = clamp(
    proposedTop,
    boundary.top,
    Math.max(boundary.top, boundary.bottom - submenuBounds.height)
  );
  const initialTopOffset = Math.round(top - triggerBounds.top);
  submenu.style.top = `${initialTopOffset}px`;
  const positionedBounds = submenu.getBoundingClientRect();
  const correctedTop = clamp(
    positionedBounds.top,
    boundary.top,
    Math.max(boundary.top, boundary.bottom - positionedBounds.height)
  );
  submenu.style.top = `${Math.round(
    initialTopOffset + correctedTop - positionedBounds.top
  )}px`;
}

function createActionLabel(label) {
  const span = document.createElement("span");
  span.textContent = label;
  return span;
}

function textValue(value) {
  const span = document.createElement("span");
  span.className = "drawing-style-text-value";
  span.textContent = value;
  return span;
}

function optionLabel(options, selected) {
  return options.find(([value]) => value === selected)?.[1] || String(selected);
}

function labelForSetting(setting) {
  return String(setting)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (value) => value.toUpperCase());
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
